import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * SAB-994: this week's projection for a set of players, from v_nfl_weekly_projections.
 *
 * The view already applies the whole rule (season / 17, zeroed on bye and non-ACT), so this
 * hook only fetches. Deriving any part of it here would fork a rule the database owns.
 *
 * ── UNDEFINED, NULL AND ZERO ARE THREE DIFFERENT ANSWERS ────────────────────
 *
 * A player absent from the map is "not loaded / not asked". A null VALUE is "no projection
 * exists for this player" -- 778 of the active board carry one, the rest do not, and those
 * must render as a dash. Zero is a real projection meaning "will not play this week".
 * Collapsing any pair of these produces a confident 0.0 for a player nobody has projected,
 * which is the failure SAB-811 called out on the season column.
 */
export function useWeeklyProjections(
  playerIds: string[],
  week: number | null | undefined,
): Map<string, number | null> {
  const [map, setMap] = useState<Map<string, number | null>>(new Map());
  const key = playerIds.length ? [...playerIds].sort().join(',') : '';

  useEffect(() => {
    let cancelled = false;
    if (!week || playerIds.length === 0) { setMap(new Map()); return; }
    (async () => {
      const { data, error } = await supabase
        .from('v_nfl_weekly_projections' as never)
        .select('player_id, weekly_proj')
        .eq('week', week)
        .in('player_id', playerIds);
      if (cancelled) return;
      if (error || !data) {
        // Stays empty, so every row renders a dash rather than a fabricated 0.0.
        console.error('[weekly-projections] read failed:', error?.message);
        setMap(new Map());
        return;
      }
      const next = new Map<string, number | null>();
      for (const r of data as unknown as { player_id: string; weekly_proj: number | null }[]) {
        next.set(r.player_id, r.weekly_proj === null ? null : Number(r.weekly_proj));
      }
      setMap(next);
    })();
    return () => { cancelled = true; };
    // Keyed on the SORTED id list, not the array identity: callers rebuild the array every
    // render, and depending on identity would refetch in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, week]);

  return map;
}

/** '--' for absent or null, one decimal otherwise. Zero renders as 0.0, which is a claim. */
export function formatWeeklyProj(v: number | null | undefined): string {
  return v === null || v === undefined ? '--' : v.toFixed(1);
}
